<template>
  <div class="landing-home">
    <!-- Hero Section -->
    <section class="hero-section hero-animate" :style="{ backgroundImage: `url(${heroImage})` }">
      <div class="hero-overlay"></div>
      <div class="container-fluid">
        <div class="container">
          <div class="row align-items-center">
          <!-- Columna Izquierda: Contenido y Botones -->
          <div class="col-lg-6 col-md-6">
            <div class="hero-content animate-on-scroll fade-in-left">
              <h1 class="hero-title">
                <span class="title-highlight">Planes Médicos</span>
                <span class="title-white">Accesibles para</span>
                <span class="title-teal">la comunidad hispana</span>
              </h1>
              <div class="hero-buttons">
                <router-link to="/plans" class="btn btn-consult-plans">
                  Consulta los planes disponibles
                </router-link>
              </div>
              <p class="hero-description">
                Un plan médico diseñado especialmente para la comunidad hispana que ofrece atención médica primaria sin copago, chequeos preventivos y grandes descuentos en servicios de salud.
              </p>
            </div>
          </div>
          
          <!-- Columna Derecha: Formulario -->
          <div class="col-lg-6 col-md-6">
            <div class="hero-form-wrapper animate-on-scroll fade-in-right">
              <ContactFormCard
                form-id="home-hero"
                :show-header="true"
                title="¿Necesitas más información?"
                subtitle="Completa el formulario y nos pondremos en contacto contigo"
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>

    <!-- Oportunidad 1.4M (mockup 2027) -->
    <OpportunitySection />

    <!-- Qué es HMP: 4 puntos cortos (mockup), sin párrafos largos -->
    <section id="que-es" class="what-is-compact-section">
      <div class="container">
        <div class="section-header mb-4">
          <h2 class="section-title">Qué es Hispano Medical Plan</h2>
        </div>
        <div class="what-is-grid">
          <div v-for="(item, i) in whatIsPoints" :key="i" class="what-is-item">
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Calculadora + planes (justo después de 1.4M / qué es) -->
    <FamilyQuoteCalculator @want-this-price="onWantFamilyPrice" />
    <PlansTabsSection ref="plansTabs" :api-plans="plans" />

    <!-- Comparar vs mercado + oferta + activación -->
    <CompareMarketSection />
    <OfferReasonsSection />
    <ActivationDisclaimersSection />

    <!-- Plans Section -->
    <section v-if="!loading && plans && plans.length > 0" id="plans" class="detailed-plans-section">
      <div class="container-fluid px-3 px-xl-4">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up" :class="{ 'is-visible': plans.length > 0 }">
          <h2 class="section-title">Detalles del Plan</h2>
          <p class="section-subtitle">
            Información detallada de cada plan disponible
          </p>
        </div>
        <div class="plans-five-row">
          <div
            v-for="(plan, index) in plans"
            :key="plan.id"
            class="plans-five-col"
          >
            <PlanDetailCard
              :plan="plan"
              :index="index"
              :is-visible="plans.length > 0"
              variant="mockup"
              @show-facilities="showFacilitiesModal"
              @show-procedures="showProceduresModal"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Empty State -->
    <section v-if="!loading && (!plans || plans.length === 0)" id="plans" class="plans-section">
      <div class="container">
        <div class="text-center py-5">
          <p class="text-muted">No hay planes disponibles en este momento.</p>
          <p v-if="error" class="text-danger mt-2">{{ error }}</p>
        </div>
      </div>
    </section>


    <!-- CTA Section -->
    <CTASection 
      :background-image="familyImage"
      container-class="col-lg-6"
    >
      <template #title>
        <h2 class="cta-title">¿Listo para <span class="cta-title-highlight">cuidar tu salud</span>?</h2>
      </template>
      <template #subtitle-large>
        <h3 class="cta-subtitle-large">Únete a Hispano Medical Plan hoy.</h3>
      </template>
      <template #description>
        <p class="cta-subtitle">
          No esperes hasta enfermarte. La prevención es la mejor inversión para ti y los tuyos.
        </p>
      </template>
      <template #buttons>
        <router-link to="/plans" class="btn btn-cta-primary btn-lg">
          ¡Hazte Miembro!
          <i class="fas fa-arrow-right ml-2"></i>
        </router-link>
      </template>
    </CTASection>

    <!-- Statistics omitidas: ruido vs mockup 2027 -->
    <StatisticsSection
      v-if="false"
      members="500+"
      years="3+"
      :locations="locations"
      :satisfaction="99"
    />

    <!-- Service Areas Section -->
    <ServiceAreasSection :locations="locations" :google-maps-url="googleMapsUrl" />

    <!-- Testimonials Section -->
    <TestimonialsSection :testimonials="testimonials" :locations="locations" />

    <!-- Contact Info Section -->
    <ContactInfoSection />

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="faq-title">Tres objeciones, tres respuestas</h2>
          <p class="faq-subtitle">
            Respuestas cortas a lo que más preguntan
          </p>
        </div>
        
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="faq-accordion">
              <div 
                v-for="(faq, index) in faqs" 
                :key="index"
                class="faq-item animate-on-scroll fade-in-up"
                :style="`animation-delay: ${0.1 + index * 0.1}s`"
              >
                <div 
                  class="faq-question"
                  @click="toggleFaq(index)"
                  :class="{ active: faq.isOpen }"
                >
                  <div class="faq-question-content">
                    <span class="faq-number">{{ index + 1 }}.</span>
                    <span class="faq-question-text">{{ faq.question }}</span>
                  </div>
                  <i 
                    class="fas fa-chevron-up faq-arrow"
                    :class="{ rotated: faq.isOpen }"
                  ></i>
                </div>
                <transition name="slide-fade">
                  <div v-show="faq.isOpen" class="faq-answer">
                    <p>{{ faq.answer }}</p>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Modales para Facilidades -->
    <b-modal 
      v-for="plan in plans"
      :key="`facilities-${plan.id}`"
      :id="`facilities-modal-${plan.id}`" 
      :title="`Clínicas - ${plan.name}`"
      size="lg"
      centered
      @hidden="selectedPlan = null"
    >
      <div v-if="plan && plan.facilities && plan.facilities.length > 0">
        <div class="facilities-grid">
          <div 
            v-for="(facility, idx) in plan.facilities" 
            :key="idx"
            class="facility-item"
          >
            <i class="fas fa-building facility-icon"></i>
            <span>{{ facility }}</span>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        <i class="fas fa-inbox fa-3x mb-3"></i>
        <p>No hay clínicas configuradas para este plan.</p>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="closeFacilitiesModal">
          Cerrar
        </b-button>
      </template>
    </b-modal>

    <!-- Modales para Beneficios -->
    <b-modal 
      v-for="plan in plans"
      :key="`procedures-${plan.id}`"
      :id="`procedures-modal-${plan.id}`" 
      :title="`Beneficios Incluidos - ${plan.name}`"
      title-class="benefits-modal-title"
      size="lg"
      centered
      @hidden="selectedPlan = null"
    >
      <div v-if="plan && plan.marketingBenefits && plan.marketingBenefits.length">
        <ul class="marketing-benefits-list">
          <li v-for="(b, idx) in plan.marketingBenefits" :key="'mkt-' + idx">
            <b>{{ b[0] }}</b>
            <span>{{ b[1] }}</span>
          </li>
        </ul>
      </div>
      <div v-else-if="plan && plan.parameters_details && plan.parameters_details.length > 0">
        <div class="benefits-grid">
          <div 
            v-for="(param, idx) in plan.parameters_details" 
            :key="idx"
            class="benefit-card benefit-card--animate"
            :style="`animation-delay: ${idx * 0.05}s`"
          >
            <div class="benefit-card__icon-wrap">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="benefit-card__content">
              <span class="benefit-card__name">
                {{ (param.parametrizable && param.parametrizable.name) || (param.memberships_parameters_types && param.memberships_parameters_types.name) || 'Beneficio' }}
              </span>
              <span class="benefit-card__qty">{{ param.count }} incl.</span>
              <span v-if="param.memberships_parameters_types && param.memberships_parameters_types.name" class="benefit-card__tag">
                {{ param.memberships_parameters_types.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        <i class="fas fa-inbox fa-3x mb-3"></i>
        <p>No hay beneficios configurados para este plan.</p>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="closeProceduresModal">
          Cerrar
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'
import { BModal, BButton } from 'bootstrap-vue'
import ToastificationContent from '@/core/plugins/toastification/ToastificationContent.vue'
import { enrichAndOrderHmpPlans } from '../data/hmpPlans2027'
import {
  ServiceAreasSection,
  ContactInfoSection,
  TestimonialsSection,
  CTASection,
  PlanDetailCard,
  StatisticsSection,
  ContactFormCard,
  PlansTabsSection,
  OpportunitySection,
  CompareMarketSection,
  OfferReasonsSection,
  ActivationDisclaimersSection,
  FamilyQuoteCalculator
} from '../components'

export default {
  name: "LandingHome",
  components: {
    BModal,
    BButton,
    ServiceAreasSection,
    ContactInfoSection,
    TestimonialsSection,
    CTASection,
    PlanDetailCard,
    StatisticsSection,
    ContactFormCard,
    PlansTabsSection,
    OpportunitySection,
    CompareMarketSection,
    OfferReasonsSection,
    ActivationDisclaimersSection,
    FamilyQuoteCalculator
  },
  data() {
    return {
      visibleElements: new Set(),
      scrollObserver: null,
      plans: [],
      loading: true,
      error: null,
      selectedPlan: null,
      heroImage: require('@/assets/lp/bannerDoc.webp'),
      corporateImage: require('@/assets/lp/docCor.png'),
      familyImage: require('@/assets/lp/fam.webp'),
      percentageIcon: require('@/assets/lp/3.png'),
      starsIcon: require('@/assets/lp/Recurso 55@300x.png'),
      googleMapsUrl: 'https://www.google.com/maps/@32.8915449,-96.9087889,11z/data=!4m2!11m1!2sXs5WthpeGtow6ZlL-JWYW8hYJXOD_A?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D',
      locations: [
        {
          name: 'Oak Cliff',
          address: '3434 W Illinois Ave Ste 202, Dallas, Texas, 75211',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'First Street Garland',
          address: '3050 S. First St, Garland, Texas, 75041',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Fort Worth',
          address: '1303 N Beach St, Fort Worth, Texas, 76111',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Garland',
          address: '3516 W Walnut St, Garland, Texas, 75042',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Buckner Blvd Dallas',
          address: '3045 N Buckner Blvd Suite 110, Dallas, Texas, 75228',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Coit',
          address: '14211 Coit Rd, Dallas, Texas, 75254',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Denton',
          address: '231 W University Dr #111, Denton, Texas, 76201',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Irving',
          address: '204 S Nursery Rd #166, Irving, Texas, 75060',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Josey Lane',
          address: '3065 N Josey Ln Apt 28, Carrollton, Texas, 75007',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'McKinney',
          address: '811 N McDonald St, McKinney, Texas, 75069',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        }
      ],
      savingsServices: [
        { name: 'Consultas presenciales ilimitadas', withoutPlan: 480, withPlan: 0 },
        { name: 'Telemedicina ilimitada', withoutPlan: 360, withPlan: 0 },
        { name: '2 Wellness Visit al año', withoutPlan: 850, withPlan: 0 },
        { name: 'PAP o PSA anual', withoutPlan: 75, withPlan: 0 },
        { name: 'Medicamentos con descuento', withoutPlan: 140, withPlan: 136 },
        { name: 'Ultrasonido anual incluido', withoutPlan: 170, withPlan: 35 }
      ],
      testimonials: [
        {
          text: "Gracias a Hispano Medical Plan, he podido llevar a mis hijos al médico sin preocuparme por los costos. El personal es amable y siempre nos atienden en español.",
          author: "María Elena Martínez"
        },
        {
          text: "Los descuentos en medicamentos han sido una gran ayuda para mi tratamiento. Ahorro hasta un 75% en mis medicamentos mensuales. Excelente servicio.",
          author: "José Antonio González"
        },
        {
          text: "La telemedicina ha sido una bendición para mi familia. Puedo consultar con médicos a cualquier hora sin tener que faltar al trabajo. ¡Totalmente recomendado!",
          author: "Ana Sofía Ramírez"
        }
      ],
      whatIsPoints: [
        {
          title: 'No es un seguro',
          text: 'Una mensualidad fija para tener médico de cabecera, prevención y descuentos. Sin deducibles ni papeleo.'
        },
        {
          title: 'Sin requisitos',
          text: 'No pedimos seguro médico, estatus migratorio ni examen previo para inscribirte.'
        },
        {
          title: '11 sedes propias',
          text: 'Atención presencial en nuestras clínicas del Metroplex y video consulta desde casa.'
        },
        {
          title: 'Desde el día 1',
          text: 'Consultas y descuentos activos desde el primer pago. Sin periodo de espera.'
        }
      ],
      faqs: [
        {
          question: '“¿Y si me enfermo de algo grave?”',
          answer: 'Este es tu médico de cabecera y tu prevención por menos de lo que cuesta una consulta. Detectar a tiempo evita que algo pequeño se vuelva grave. Para emergencias y hospital, el plan no cubre: llama al 911.',
          isOpen: false
        },
        {
          question: '“¿Me van a pedir papeles?”',
          answer: 'No pedimos seguro ni estatus migratorio. Solo tus datos y un método de pago.',
          isOpen: false
        },
        {
          question: '“Ahora no tengo dinero.”',
          answer: 'Una consulta sin seguro suele costar más que un mes del plan. Y si no te convence en 30 días, y no usaste exámenes ni estudios, te devolvemos tu primer mes.',
          isOpen: false
        }
      ],
      coverageBenefits: [
        {
          image: require('@/assets/lp/Recurso 21@300x.png'),
          title: 'Consultas y Videoconsultas',
          description: 'Cubiertas en clínicas afiliadas. Acceso ilimitado a profesionales médicos sin costo por visita. Perfecto para seguimiento continuo y resolver consultas sin gastos adicionales.',
          badge: '100% CUBIERTO',
          badgeClass: 'badge-success'
        },
        {
          image: require('@/assets/lp/Recurso 22@300x.png'),
          title: '2 Wellness Visit Anual',
          description: '2 evaluaciones completas al año (100% cubiertas). Incluyen análisis de sangre con 32 parámetros, HbA1c, EKG, análisis de orina y ultrasonido de mama o abdominal según necesidad.',
          badge: '2 AL AÑO ($0)',
          badgeClass: 'badge-success'
        },
        {
          image: require('@/assets/lp/Recurso 23@300x.png'),
          title: 'Exámenes Especiales',
          description: 'PAP para mujeres y Antígeno Prostático PSA para hombres dentro de la evaluación anual. Estudios esenciales para prevención y detección temprana.',
          badge: 'INCLUIDO',
          badgeClass: 'badge-success'
        },
        {
          image: require('@/assets/lp/Recurso 24@300x.png'),
          title: 'Descuento en Medicamentos',
          description: 'Hasta 80% de descuento en medicamentos y suplementos. Opción de entrega a domicilio o recogida en clínica para evitar interrupciones.',
          badge: 'DELIVERY',
          badgeClass: 'badge-primary'
        },
        {
          image: require('@/assets/lp/Recurso 25@300x.png'),
          title: 'Beneficios en Diagnósticos de Imagen',
          description: 'Rayos X y ultrasonido abdominal, una vez al año. Estudios de imagen esenciales para diagnóstico y seguimiento de tu salud.',
          badge: '1 AL AÑO',
          badgeClass: 'badge-success'
        }
      ]
    };
  },
  watch: {
    plans: {
      handler(newPlans) {
        if (newPlans && newPlans.length > 0) {
          console.log('Planes actualizados en watcher:', newPlans);
          this.$nextTick(() => {
            // Forzar actualización del select
            const select = this.$el.querySelector('#planInterest');
            if (select) {
              select.dispatchEvent(new Event('change'));
            }
          });
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    hasPlans() {
      return Array.isArray(this.plans) && this.plans.length > 0;
    },
    facilitiesModalId() {
      return this.selectedPlan && this.selectedPlan.id 
        ? `facilities-modal-${this.selectedPlan.id}` 
        : 'facilities-modal-default';
    },
    facilitiesModalTitle() {
      return this.selectedPlan && this.selectedPlan.name 
        ? `Clínicas - ${this.selectedPlan.name}` 
        : 'Clínicas';
    },
    proceduresModalId() {
      return this.selectedPlan && this.selectedPlan.id 
        ? `procedures-modal-${this.selectedPlan.id}` 
        : 'procedures-modal-default';
    },
    proceduresModalTitle() {
      return this.selectedPlan && this.selectedPlan.name 
        ? `Beneficios Incluidos - ${this.selectedPlan.name}` 
        : 'Beneficios Incluidos';
    }
  },
  mounted() {
    this.loadPlans();
    this.setupScrollAnimations();
    
    // Manejar hash #contact para redirigir a la página de contacto
    if (window.location.hash === '#contact') {
      this.$router.push('/contact');
      return
    }
    this.$nextTick(() => {
      this.scrollToHashSection()
    })
  },
  beforeDestroy() {
    // Limpiar el observer cuando el componente se destruye
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
    this.visibleElements.clear();
  },
  methods: {
    /**
     * CTA de la calculadora: abre compra de Tu Salud (1) o Familiar (2+).
     * @param {{ totalPeople: number }} payload
     */
    onWantFamilyPrice(payload) {
      const total = (payload && payload.totalPeople) || 1
      const tabs = this.$refs.plansTabs
      if (tabs && typeof tabs.openFromCalculator === 'function') {
        tabs.openFromCalculator(total)
        return
      }
      this.$router.push({ path: '/', hash: 'planes-2027' }).catch(() => {})
    },
    /**
     * Scroll a anclas del mockup 2027 (#oportunidad, #comparar, etc.).
     * El layout LP hace scroll en document.body, no en window.
     */
    scrollToHashSection() {
      const hash = (this.$route.hash || window.location.hash || '').replace(/^#/, '')
      if (!hash) return
      const el = document.getElementById(hash)
      if (!el) return
      const top = el.getBoundingClientRect().top + (document.body.scrollTop || window.pageYOffset || 0) - 80
      if (document.body && typeof document.body.scrollTo === 'function') {
        document.body.scrollTo({ top, behavior: 'smooth' })
      } else {
        window.scrollTo({ top, behavior: 'smooth' })
      }
    },
    /**
     * Carga los planes desde el API
     */
    async loadPlans() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await plansService.getAllPlans();
        
        let plansData = [];
        
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data;
        } else if (Array.isArray(response)) {
          plansData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data;
        }
        
        const ordered = enrichAndOrderHmpPlans(plansData)
        this.plans = ordered.length ? ordered : plansData
        
        // Debug: verificar que los planes se cargaron
        console.log('Planes cargados:', this.plans);
        console.log('Cantidad de planes:', this.plans.length);
        
        // Esperar a que Vue renderice los nuevos elementos
        await this.$nextTick();
        await this.$nextTick(); // Doble nextTick para asegurar que el DOM esté actualizado
        
        // Asegurar que el observer esté inicializado
        if (!this.scrollObserver) {
          this.setupScrollAnimations();
        }
        
        // Agregar elementos de planes al observer para que se animen cuando entren en viewport
        this.$nextTick(() => {
          setTimeout(() => {
            const planElements = this.$el.querySelectorAll('.detailed-plan-card.animate-on-scroll, .detailed-plans-section .section-header.animate-on-scroll');
            planElements.forEach(el => {
              // Asegurar que empiecen sin la clase visible para que se animen
              el.classList.remove('is-visible');
              // Agregar al observer para que se animen cuando entren en viewport
              if (this.scrollObserver) {
                this.scrollObserver.observe(el);
              }
              
              // Si ya está en viewport, animarlo inmediatamente
              const rect = el.getBoundingClientRect();
              const isInViewport = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
              if (isInViewport) {
                setTimeout(() => {
                  el.classList.add('is-visible');
                  this.visibleElements.add(el);
                }, 150);
              }
            });
          }, 100);
        });
      } catch (error) {
        console.error('Error loading plans:', error);
        this.error = 'No se pudieron cargar los planes. Por favor, intenta de nuevo más tarde.';
        this.plans = [];
      } finally {
        this.loading = false;
      }
    },
    setupScrollAnimations() {
      // Configuración mejorada para detectar elementos en ambas direcciones
      const observerOptions = {
        threshold: 0.15, // Se activa cuando el 15% del elemento es visible
        rootMargin: '0px 0px -80px 0px' // Activa antes de que el elemento esté completamente visible
      };

      // Crear observer si no existe
      if (!this.scrollObserver) {
        this.scrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            // Activar animación cuando el elemento entra en el viewport
            if (entry.isIntersecting) {
              // Usar requestAnimationFrame para suavizar la animación
              requestAnimationFrame(() => {
                entry.target.classList.add('is-visible');
                this.visibleElements.add(entry.target);
              });
            } else {
              // Remover la clase cuando el elemento sale del viewport para que se repita la animación
              requestAnimationFrame(() => {
                entry.target.classList.remove('is-visible');
                this.visibleElements.delete(entry.target);
              });
            }
          });
        }, observerOptions);
      }

      // Función para inicializar observación de elementos
      const initObserver = () => {
        const elements = this.$el.querySelectorAll('.animate-on-scroll');
        elements.forEach((el) => {
          // Asegurar que todos los elementos empiecen sin la clase visible
          el.classList.remove('is-visible');
          // Observar cada elemento (puede estar siendo observado múltiples veces, pero el observer lo maneja)
          this.scrollObserver.observe(el);
        });
      };

      // Observar elementos después de que el DOM esté listo
      this.$nextTick(() => {
        initObserver();
        
        // Verificar elementos que ya están visibles al cargar
        const elements = this.$el.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => {
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
          if (isInViewport) {
            setTimeout(() => {
              el.classList.add('is-visible');
              this.visibleElements.add(el);
            }, 100);
          }
        });
      });
    },
    showFacilitiesModal(plan) {
      if (!plan || !plan.id) {
        console.error('Plan inválido:', plan);
        return;
      }
      this.selectedPlan = plan;
      this.$nextTick(() => {
        const modalId = `facilities-modal-${plan.id}`;
        console.log('Mostrando modal:', modalId);
        this.$bvModal.show(modalId);
      });
    },
    showProceduresModal(plan) {
      if (!plan || !plan.id) {
        console.error('Plan inválido:', plan);
        return;
      }
      this.selectedPlan = plan;
      this.$nextTick(() => {
        const modalId = `procedures-modal-${plan.id}`;
        console.log('Mostrando modal:', modalId);
        this.$bvModal.show(modalId);
      });
    },
    closeFacilitiesModal() {
      if (this.selectedPlan && this.selectedPlan.id) {
        this.$bvModal.hide(`facilities-modal-${this.selectedPlan.id}`);
      }
    },
    closeProceduresModal() {
      if (this.selectedPlan && this.selectedPlan.id) {
        this.$bvModal.hide(`procedures-modal-${this.selectedPlan.id}`);
      }
    },
    toggleFaq(index) {
      this.faqs[index].isOpen = !this.faqs[index].isOpen;
    }
  },
  computed: {
    totalWithoutPlan() {
      return this.savingsServices.reduce((sum, service) => sum + service.withoutPlan, 0);
    },
    totalWithPlan() {
      return this.savingsServices.reduce((sum, service) => sum + service.withPlan, 0);
    },
    potentialSavings() {
      return this.totalWithoutPlan - this.totalWithPlan;
    },
    savingsPercentage() {
      return Math.round((this.potentialSavings / this.totalWithoutPlan) * 100);
    }
  }
};
</script>

<style lang="scss" scoped>
// Importar variables de color centralizadas
@import '../styles/variables';

.landing-home {
  .what-is-compact-section {
    padding: 64px 0 24px;
    background: $landing-bg-light;

    .section-title {
      color: $landing-primary-color;
      font-size: clamp(1.75rem, 3vw, 2.5rem);
      font-weight: 800;
      margin-bottom: 0;
    }

    .what-is-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      border-top: 2px solid $landing-text-dark;
      margin-top: 1.5rem;
    }

    .what-is-item {
      padding: 1.25rem 1.25rem 0.5rem 0;

      & + .what-is-item {
        padding-left: 1.25rem;
        border-left: 1px solid $landing-border-light;
      }

      h3 {
        font-size: 1.1rem;
        font-weight: 750;
        color: $landing-text-dark;
        margin-bottom: 0.4rem;
      }

      p {
        margin: 0;
        color: $landing-text-medium;
        font-size: 0.95rem;
        line-height: 1.45;
      }
    }

    @media (max-width: 991.98px) {
      .what-is-grid {
        grid-template-columns: 1fr 1fr;
      }

      .what-is-item:nth-child(3) {
        padding-left: 0;
        border-left: 0;
      }
    }

    @media (max-width: 575.98px) {
      .what-is-grid {
        grid-template-columns: 1fr;
      }

      .what-is-item + .what-is-item {
        padding-left: 0;
        border-left: 0;
        border-top: 1px solid $landing-border-light;
        padding-top: 1rem;
      }
    }
  }

  .hero-section {
    position: relative;
    min-height: 700px;
    padding: 100px 0;
    color: white;
    display: flex;
    align-items: center;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    
    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 60%;
      height: 100%;
      background: transparent;
      z-index: 1;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: transparent;
        z-index: 2;
      }
      
      @media (max-width: 991px) {
        width: 80%;
      }
      
      @media (max-width: 768px) {
        width: 100%;
        background: transparent;
      }
    }
    
    .container-fluid {
      padding-left: 60px;
      padding-right: 60px;
      
      .container {
        position: relative;
        z-index: 3;
        max-width: 1400px;
      }
    }
    
    .hero-content {
      position: relative;
      padding-right: 50px;
      padding-left: 20px;
      z-index: 3;
    }
    
    .hero-title {
      font-size: 4.5rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      line-height: 1.15;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(13, 43, 69, 0.15);
      letter-spacing: -1px;
      
      .title-highlight {
        color: white;
        display: block;
        margin-bottom: 0.5rem;
        font-size: 5rem;
        text-shadow: 0 3px 12px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
        position: relative;
        background: none;
        -webkit-background-clip: unset;
        -webkit-text-fill-color: white;
        background-clip: unset;
        filter: none;
      }
      
      .title-white {
        display: block;
        color: white;
        font-size: 5rem;
        font-weight: 700;
        line-height: 1.15;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(13, 43, 69, 0.15);
        letter-spacing: -1px;
        margin-bottom: 0;
      }
      
      .title-teal {
        display: block;
        color: $landing-secondary-color;
        font-size: 5rem;
        font-weight: 700;
        line-height: 1.15;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(34, 207, 224, 0.3);
        letter-spacing: -1px;
        margin-bottom: 1.5rem;
      }
    }
    
    .hero-subtitle {
      font-size: 3rem;
      margin-bottom: 1.75rem;
      opacity: 1;
      line-height: 1.4;
      color: white;
      text-shadow: 0 4px 15px rgba(13, 43, 69, 0.45), 0 2px 10px rgba(0, 0, 0, 0.25);
      font-weight: 700;
      letter-spacing: -0.5px;
    }
    
    .hero-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 15px;
      margin-bottom: 1.5rem;
      
      .btn {
        padding: 15px 35px;
        font-size: 1.1rem;
        border-radius: 50px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        border: none;
        
        &.btn-consult-plans {
          background: $landing-secondary-color;
          color: $landing-primary-color;
          font-size: 2.1rem;
          font-weight: 600;
          padding: 18px 45px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(34, 207, 224, 0.3);
          transition: all 0.3s ease;
          white-space: nowrap;
          
          &:hover {
            background: #00ACC1;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4);
            color: $landing-primary-color;
            text-decoration: none;
          }
        }
        
        &.btn-primary-hero {
          background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
          padding: 22px 50px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          box-shadow: 0 10px 30px rgba(13, 43, 69, 0.45), 0 5px 15px rgba(13, 43, 69, 0.2);
          letter-spacing: 0.5px;
          
          &:hover {
            background: linear-gradient(135deg, #123a5c 0%, #0d2b45 100%);
            transform: translateY(-4px) scale(1.02);
            box-shadow: 0 15px 40px rgba(13, 43, 69, 0.55), 0 8px 20px rgba(13, 43, 69, 0.25);
            color: white;
            text-decoration: none;
          }
          
          i {
            font-size: 1.5rem;
            margin-left: 8px;
          }
        }
        
        i {
          transition: transform 0.3s ease;
        }
        
        &:hover i {
          transform: translateX(5px);
        }
      }
    }
    
    .hero-description {
      font-size: 1.75rem;
      margin-bottom: 2.5rem;
      line-height: 1.7;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      font-weight: 500;
      opacity: 1;
      max-width: 90%;
      letter-spacing: 0.1px;
    }
    
    // Formulario de contacto
    .hero-form-wrapper {
      position: relative;
      margin-left: 60px;
      padding-left: 10px;
      z-index: 3;
    }
    
    @keyframes shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
    
    .container-fluid {
      @media (max-width: 1200px) {
        padding-left: 40px;
        padding-right: 40px;
      }
      
      @media (max-width: 991px) {
        padding-left: 30px;
        padding-right: 30px;
      }
      
      @media (max-width: 768px) {
        padding-left: 20px;
        padding-right: 20px;
      }
    }
    
    @media (max-width: 991px) {
      min-height: auto;
      padding: 80px 0;

      .hero-content {
        max-width: 100%;
        padding-right: 20px;
        padding-left: 10px;
        margin-bottom: 2rem;
      }

      .hero-title {
        font-size: 2.5rem;
      }

      .hero-subtitle {
        font-size: 1.3rem;
      }

      .hero-description {
        font-size: 1rem;
      }

      .hero-form-wrapper {
        padding-left: 0;
        margin-left: 0;
      }
    }

    /* Solo tablet (ej. iPad Air): letras más pequeñas, sin tocar desktop ni móvil */
    @media (min-width: 769px) and (max-width: 991px) {
      .hero-title {
        font-size: 2rem;
        margin-bottom: 1rem;

        .title-highlight,
        .title-white,
        .title-teal {
          font-size: 2.2rem;
        }
      }

      .hero-description {
        font-size: 0.95rem;
        margin-bottom: 1.5rem;
      }

      .hero-buttons .btn.btn-consult-plans {
        font-size: 1.35rem;
        padding: 14px 28px;
      }

      .contact-form-card {
        .form-title {
          font-size: 1.15rem;
        }

        .form-subtitle {
          font-size: 0.8rem;
        }

        .contact-form .form-group .form-label {
          font-size: 0.85rem;
        }
      }
    }

    @media (max-width: 768px) {
      .hero-form-wrapper {
        padding-left: 0;
        margin-left: 0;
      }
      
      .contact-form-card {
        padding: 1.75rem 1.5rem;
        border-radius: 15px;
        
        .form-header {
          margin-bottom: 1.5rem;
          
          .form-title {
            font-size: 1.4rem;
            margin-bottom: 0.4rem;
          }
          
          .form-subtitle {
            font-size: 0.85rem;
          }
        }
        
        .contact-form {
          .form-group {
            margin-bottom: 1.25rem;
            
            .form-label {
              font-size: 0.85rem;
              margin-bottom: 0.6rem;
              
              .form-icon {
                font-size: 0.8rem;
              }
            }
            
            .input-wrapper,
            .select-wrapper {
              .form-control {
                padding: 12px 14px;
                font-size: 0.9rem;
                border-radius: 8px;
              }
            }
            
            &.checkbox-group {
              margin-bottom: 1.5rem;
              
              .custom-checkbox {
                .checkbox-label {
                  gap: 10px;
                  
                  .checkbox-custom {
                    width: 20px;
                    height: 20px;
                    margin-top: 1px;
                  }
                  
                  .checkbox-text {
                    font-size: 0.75rem;
                    line-height: 1.5;
                  }
                }
              }
            }
          }
          
          .btn-submit-form {
            padding: 14px 25px;
            font-size: 1rem;
            border-radius: 10px;
          }
        }
      }
      min-height: auto;
      padding: 60px 0;
      
      .hero-title {
        font-size: 2.5rem;
        margin-bottom: 1rem;
        
        .title-highlight {
          display: inline;
          font-size: 2.8rem;
        }
        
        .title-white {
          font-size: 2.5rem;
        }
        
        .title-teal {
          font-size: 2.5rem;
        }
      }
      
      .hero-subtitle {
        font-size: 1.6rem;
        margin-bottom: 1rem;
      }
      
      .hero-description {
        font-size: 1.1rem;
        margin-bottom: 2rem;
      }
      
      .hero-buttons {
        flex-direction: column;
        
        .btn {
          width: 100%;
          justify-content: center;
          
          &.btn-consult-plans {
            font-size: 1.1rem;
            padding: 12px 24px;
            color: $landing-primary-color;
            background: $landing-secondary-color;
          }
        }
      }
      
      .contact-form-card {
        padding: 1.5rem;
        
        .form-title {
          font-size: 1.25rem;
          margin-bottom: 1.25rem;
        }
        
        .contact-form {
          .form-group {
            margin-bottom: 1rem;
            
            .form-label {
              font-size: 0.9rem;
            }
            
            .form-control {
              padding: 10px 12px;
              font-size: 0.9rem;
            }
            
            &.checkbox-group {
              .checkbox-label {
                .checkbox-text {
                  font-size: 0.8rem;
                }
              }
            }
          }
          
          .btn-submit-form {
            padding: 14px 25px;
            font-size: 1rem;
          }
        }
      }
      
      background-position: center;
    }
  }
  
  .corporate-banner {
    background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
    padding: 35px 0;
    color: white;
    
    .banner-item {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 15px;
      font-weight: 600;
      font-size: 1.5rem;
      transition: all 0.3s ease;
      letter-spacing: 0.2px;
      
      span {
        font-size: 1.5rem;
        font-weight: 600;
        text-shadow: 0 2px 8px rgba(13, 43, 69, 0.3);
      }
      
      &:hover {
        transform: translateY(-4px);
        
        i {
          transform: scale(1.25);
          color: white;
        }
        
        span {
          text-shadow: 0 3px 12px rgba(13, 43, 69, 0.4);
        }
      }
      
      i {
        font-size: 1.8rem;
        color: rgba(255, 255, 255, 0.95);
        transition: all 0.3s ease;
        filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2));
      }
    }
    
    @media (max-width: 768px) {
      padding: 30px 0;
      
      .banner-item {
        font-size: 1.2rem;
        gap: 12px;
        
        span {
          font-size: 1.2rem;
        }
        
        i {
          font-size: 1.5rem;
        }
      }
    }
    
    @media (max-width: 576px) {
      padding: 25px 0;
      
      .banner-item {
        font-size: 1.1rem;
        flex-direction: column;
        gap: 8px;
        
        span {
          font-size: 1.1rem;
        }
        
        i {
          font-size: 1.4rem;
        }
      }
    }
  }
  
  .corporate-plan-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #ffffff 0%, #F8F9FA 50%, #ffffff 100%);
    
    .row {
      align-items: stretch;
    }
    
    .plan-content {
      .plan-subtitle {
        color: #5A6D70;
        font-size: 0.95rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 1rem;
      }
      
      .plan-title {
        font-size: 3.5rem;
        font-weight: 700;
        color: #0d2b45;
        margin-bottom: 2rem;
        line-height: 1.2;
      }
      
      .plan-description {
        font-size: 1.4rem;
        color: #2D4A4D;
        line-height: 1.8;
        margin-bottom: 3rem;
        font-weight: 500;
      }
      
      .benefits-list {
        display: flex;
        flex-direction: column;
        
        .benefit-item {
          display: flex;
          gap: 20px;
          margin-bottom: 2.5rem;
          padding: 20px;
          border-radius: 12px;
          background: transparent;
          transition: all 0.3s ease;
          min-height: 150px;
          align-items: center;
          
          &:hover {
            transform: translateX(5px);
            
            .benefit-icon {
              transform: scale(1.05);
            }
          }
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .benefit-icon {
            flex-shrink: 0;
            width: 80px;
            height: 80px;
            background: transparent;
            border: none;
            border-radius: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            padding: 0;
            box-shadow: none;
            transform: translateX(-10px);
            
            img {
              width: 120%;
              height: 120%;
              object-fit: contain;
            }
            
            i {
              font-size: 1.5rem;
              color: white;
              transition: transform 0.3s ease;
            }
          }
          
          .benefit-content {
            flex: 1;
            background: white;
            border: 2px solid $landing-border-light;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 4px 12px rgba(13, 43, 69, 0.2);
            
            .benefit-title {
              font-size: 1.6rem;
              font-weight: 700;
              color: #2D4A4D;
              margin-bottom: 1rem;
              line-height: 1.3;
            }
            
            .benefit-text {
              font-size: 1.2rem;
              color: #2D4A4D;
              line-height: 1.7;
              font-weight: 500;
            }
          }
        }
      }
    }
    
    .plan-image-wrapper {
      position: relative;
      transition: transform 0.3s ease;
      height: 100%;
      display: flex;
      flex-direction: column;
      
      &:hover {
        transform: translateY(-5px);
        
        .plan-image {
          box-shadow: 0 15px 50px rgba(13, 43, 69, 0.15);
        }
      }
      
      .plan-image {
        width: 100%;
        height: auto;
        border-radius: 15px;
        box-shadow: 0 10px 40px rgba(13, 43, 69, 0.1);
        object-fit: cover;
        transition: all 0.3s ease;
      }
      
      .corporate-image-clean {
        width: 100%;
        height: 100%;
        object-fit: cover;
        flex: 1;
      }
      
      .btn-corporate-plan {
        position: absolute;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
        color: white;
        border: none;
        border-radius: 0;
        padding: 15px 35px;
        font-weight: 600;
        font-size: 1.1rem;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        box-shadow: 0 8px 25px rgba(13, 43, 69, 0.4);
        transition: all 0.3s ease;
        
        &:hover {
          background: linear-gradient(135deg, #123a5c 0%, #00ACC1 100%);
          transform: translateX(-50%) translateY(-3px);
          box-shadow: 0 12px 35px rgba(13, 43, 69, 0.5);
          color: white;
          text-decoration: none;
        }
        
        i {
          transition: transform 0.3s ease;
        }
        
        &:hover i {
          transform: translateX(5px);
        }
      }
    }
    
    @media (max-width: 991px) {
      padding: 80px 0;
      
      .plan-content {
        .plan-title {
          font-size: 2rem;
        }
        
        .plan-description {
          font-size: 1rem;
        }
      }
    }
    
    @media (max-width: 768px) {
      padding: 60px 0;
      
      .plan-content {
        .plan-title {
          font-size: 1.75rem;
        }
        
        .benefits-list .benefit-item {
          flex-direction: column;
          gap: 15px;
          margin-bottom: 2rem;
          
          .benefit-icon {
            width: 100px;
            height: 100px;
            
            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
            
            i {
              font-size: 1.3rem;
            }
          }
          
          .benefit-content {
            padding: 15px;
          }
        }
      }
      
      .plan-image-wrapper .btn-corporate-plan {
        position: relative;
        bottom: auto;
        left: auto;
        transform: none;
        margin-top: 20px;
        width: 100%;
        justify-content: center;
      }
    }
  }
  
  .benefits-coverage-section {
    padding: 100px 0;
    background: linear-gradient(to bottom right, #123a5c 0%, #123a5c 60%, #22cfe0 100%);
    color: #2D4A4D;
    
    .coverage-benefits-row {
      display: flex;
      flex-wrap: nowrap;
      
      @media (max-width: 991px) {
        flex-wrap: wrap;
      }
    }
    
    .section-header {
      margin-bottom: 4rem;
      margin-top: 3rem;
      padding-top: 2rem;
    }
    
    .coverage-title {
      font-size: 4rem;
      font-weight: 700;
      color: white;
      margin-bottom: 2rem;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(13, 43, 69, 0.15);
      line-height: 1.2;
    }
    
    .coverage-subtitle {
      font-size: 2.2rem;
      color: white;
      max-width: 1100px;
      margin: 0 auto;
      line-height: 1.8;
      font-weight: 400;
      letter-spacing: 0.1px;
      text-shadow: 0 3px 12px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
    }
    
      .coverage-card {
        background: linear-gradient(to bottom right, #ffffff 0%, #ffffff 92%, #fafafa 95%, #f0f0f0 97%, #e0e0e0 98.5%, #c0c0c0 99.3%, #808080 99.7%, #2C2C2C 100%);
        border-radius: 15px;
        padding: 2rem;
        height: 100%;
        display: flex;
        flex-direction: column;
        transition: all 0.3s ease;
        border: 2px solid #0d2b45;
        box-shadow: 0 4px 12px rgba(13, 43, 69, 0.2);
        
        &:hover {
          transform: translateY(-5px);
          
          .coverage-icon {
            transform: scale(1.05);
          }
        }
      
      .coverage-icon {
        width: 80px;
        height: 80px;
        background: transparent;
        border: none;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        transition: all 0.3s ease;
        padding: 8px;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        i {
          font-size: 2rem;
          color: white;
        }
      }
      
      .coverage-card-title {
        font-size: 1.6rem;
        font-weight: 700;
        color: #0d2b45;
        margin-bottom: 1.25rem;
        line-height: 1.3;
      }
      
      .coverage-card-text {
        font-size: 1.15rem;
        color: #0d2b45;
        line-height: 1.7;
        margin-bottom: 1.5rem;
        flex-grow: 1;
        font-weight: 500;
      }
      
      .coverage-badge {
        display: inline-block;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 0.85rem;
        font-weight: 600;
        text-align: center;
        width: 100%;
        
        &.badge-success {
          background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
          color: white;
        }
        
        &.badge-info {
          background: #6993FF;
          color: white;
        }
        
        &.badge-warning {
          background: #FFA800;
          color: white;
        }
        
        &.badge-primary {
          background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
          color: white;
          background: #0d2b45;
          color: white;
        }
        
        &.badge-secondary {
          background: #6c757d;
          color: white;
        }
      }
    }
    
    @media (max-width: 991px) {
      padding: 80px 0;
      
      .coverage-title {
        font-size: 2.5rem;
      }
      
      .coverage-subtitle {
        font-size: 1.5rem;
      }
    }
    
    @media (max-width: 768px) {
      padding: 60px 0;
      
      .coverage-title {
        font-size: 2rem;
      }
      
      .coverage-subtitle {
        font-size: 1.3rem;
      }
      
      .coverage-card {
        padding: 1.5rem;
        
        .coverage-icon {
          width: 60px;
          height: 60px;
          margin-bottom: 1rem;
          
          i {
            font-size: 1.5rem;
          }
        }
        
        .coverage-card-title {
          font-size: 1.1rem;
        }
        
        .coverage-card-text {
          font-size: 0.9rem;
        }
      }
    }
  }
  
  .benefits-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #F3F6F9 0%, #ffffff 50%, #F3F6F9 100%);
    
    .section-title {
      font-size: 3rem;
      font-weight: 700;
      color: #0d2b45;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }
    
    .section-subtitle {
      font-size: 1.4rem;
      color: #2D4A4D;
      max-width: 800px;
      margin: 0 auto;
      line-height: 1.7;
      font-weight: 500;
    }
    
    .row {
      align-items: stretch;
    }
    
    .benefits-cards-wrapper {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }
    
    .benefit-card {
      background: white;
      padding: 2.5rem;
      border-radius: 15px;
      transition: all 0.3s ease;
      border: 2px solid $landing-border-light;
      display: flex;
      flex-direction: column;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        
        .benefit-icon {
          transform: scale(1.05);
        }
      }
      
      .benefit-icon {
        width: 80px;
        height: 80px;
        background: transparent;
        border: none;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        transition: transform 0.3s ease;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        i {
          font-size: 2rem;
          color: white;
          transition: transform 0.3s ease;
        }
      }
      
      .benefit-title {
        font-size: 1.75rem;
        font-weight: 700;
        color: #0d2b45;
        margin-bottom: 1.25rem;
        line-height: 1.3;
      }
      
      .benefit-text {
        color: #2D4A4D;
        line-height: 1.7;
        font-size: 1.1rem;
        font-weight: 400;
      }
    }
    
    .benefits-image-wrapper {
      position: relative;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .benefits-man-image {
        width: 100%;
        height: auto;
        object-fit: contain;
      }
    }
  }
  
  .plans-section {
    padding: 80px 0;
    background: #F3F6F9;
  }
  
  .detailed-plans-section {
    padding: 80px 0;
    background: #e6f1f3;
    
    .section-header {
      margin-bottom: 2.5rem;
      max-width: 760px;
      margin-left: auto;
      margin-right: auto;
      
      .section-title {
        color: #0d2b45;
        font-size: clamp(1.85rem, 3.5vw, 2.75rem);
        font-weight: 800;
        margin-bottom: 0.75rem;
      }
      
      .section-subtitle {
        color: #4a5d6b;
        font-size: 1.15rem;
        font-weight: 500;
        line-height: 1.55;
      }
    }

    .plans-five-row {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 1rem;
      align-items: stretch;
    }

    .plans-five-col {
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    @media (max-width: 1199.98px) {
      .plans-five-row {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    @media (max-width: 767.98px) {
      .plans-five-row {
        grid-template-columns: 1fr;
      }
    }
    
    .plan-card-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    
    .detailed-plan-card {
      background: linear-gradient(135deg, #ffffff 0%, #F8F9FA 100%);
      border: 2px solid #0d2b45;
      border-radius: 15px;
      padding: 2rem;
      position: relative;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      flex: 1;
      display: flex;
      flex-direction: column;
      
      &.plan-with-gradient {
        background: #ffffff;
        border-color: #0d2b45;
        
        .plan-header {
          background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
          border-radius: 15px 15px 0 0;
          margin: -2rem -2rem 2rem -2rem;
          padding: 2rem 2rem 1.5rem 2rem;
          border-bottom: 3px solid #0d2b45;
          color: white;
          
          .plan-name {
            color: white;
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
            margin-top: 1rem;
          }
          
          .plan-price-large {
            display: flex;
            align-items: baseline;
            justify-content: center;
            
            .currency {
              font-size: 3.5rem;
              color: white;
              line-height: 1;
              vertical-align: baseline;
            }
            
            .amount {
              font-size: 7rem;
              color: white;
              line-height: 1;
            }
            
            .period {
              font-size: 2rem;
              color: rgba(255, 255, 255, 0.9);
              line-height: 1;
              vertical-align: baseline;
            }
          }
          
          .plan-meta {
            .meta-item {
              color: rgba(255, 255, 255, 0.9);
              
              i {
                color: rgba(255, 255, 255, 0.8);
              }
            }
          }
        }
        
        .plan-body {
          .plan-statistics {
            .stat-item {
              background: #F8F9FA;
              
              &:hover {
                background: #E8F4F8;
              }
              
              .stat-number {
                color: #0d2b45;
              }
              
              .stat-label {
                color: #5A6D70;
              }
              
              .btn-outline-primary {
                border-color: #0d2b45;
                color: #0d2b45;
                display: inline-flex;
                align-items: center;
                white-space: nowrap;
                
                i {
                  margin-right: 0.5rem;
                }
                
                &:hover {
                  background: #0d2b45;
                  color: white;
                  
                  i {
                    margin-right: 0.5rem;
                  }
                }
              }
            }
          }
          
          .btn-primary {
            background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
            color: white;
            border-color: #0d2b45;
            
            &:hover {
              background: linear-gradient(135deg, #123a5c 0%, #0d2b45 100%);
              color: white;
            }
          }
        }
      }
      
      &:hover {
        border-color: #0d2b45;
        box-shadow: 0 15px 40px rgba(13, 43, 69, 0.85);
        transform: translateY(-8px);
        background: linear-gradient(135deg, #ffffff 0%, #E8F4F8 100%);
        
        &.plan-with-gradient {
          background: #ffffff;
          
          .plan-header {
            background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
          }
        }
      }
      
      .plan-badge {
        position: absolute;
        top: -15px;
        left: 50%;
        transform: translateX(-50%);
        background: #FFFFFF;
        color: #0d2b45;
        border: 2px solid #0d2b45;
        padding: 10px 24px;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: 700;
        box-shadow: 0 4px 12px rgba(13, 43, 69, 0.2);
        letter-spacing: 0.3px;
        z-index: 2;
        
        i {
          font-size: 1.1rem;
          color: #0d2b45;
        }
      }
      
      .plan-header {
        text-align: center;
        margin-bottom: 2rem;
        margin-top: 2rem;
        padding-bottom: 1.5rem;
        padding-top: 2rem;
        border-bottom: 3px solid #0d2b45;
        
        .plan-name {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D4A4D;
          margin-bottom: 1.5rem;
          margin-top: 1rem;
        }
        
        .plan-price-large {
          margin-bottom: 1rem;
          display: flex;
          align-items: baseline;
          justify-content: center;
          
          .currency {
            font-size: 3.5rem;
            vertical-align: baseline;
            color: #0d2b45;
            line-height: 1;
          }
          
          .amount {
            font-size: 7rem;
            font-weight: 700;
            color: #0d2b45;
            line-height: 1;
          }
          
          .period {
            font-size: 2rem;
            color: #5A6D70;
            line-height: 1;
            vertical-align: baseline;
          }
        }
        
        .plan-meta {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          
          .meta-item {
            color: #5A6D70;
            font-size: 0.9rem;
            
            i {
              color: #0d2b45;
            }
          }
        }
      }
      
      .plan-body {
        .plan-statistics {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          align-items: stretch;
          
          .stat-item {
            text-align: center;
            padding: 1.5rem;
            background: #F8F9FA;
            border-radius: 10px;
            transition: all 0.3s ease;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 180px;
            
            &:hover {
              background: #E8F4F8;
              transform: translateY(-2px);
            }
            
            .stat-number {
              font-size: 2.5rem;
              font-weight: 700;
              color: #0d2b45;
              line-height: 1;
              margin-bottom: 0.5rem;
            }
            
            .stat-label {
              font-size: 0.9rem;
              color: #5A6D70;
              margin-bottom: 1rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              min-height: 2.5rem;
              display: flex;
              align-items: center;
              justify-content: center;
              line-height: 1.3;
            }
            
            .btn-outline-primary {
              margin-top: auto;
              width: 100%;
              justify-content: center;
            }
          }
        }
        
        .plan-stars {
          text-align: center;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          
          .stars-image {
            width: 100%;
            max-width: 200px;
            height: auto;
            object-fit: contain;
          }
        }
      }
    }
    
    .plan-action-btn {
      background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%) !important;
      color: white !important;
      border: none !important;
      padding: 16px 32px !important;
      font-size: 1.2rem !important;
      border-radius: 12px !important;
      margin-top: 1rem;
      width: 100%;
      font-weight: 700 !important;
      transition: all 0.3s ease !important;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(13, 43, 69, 0.4) !important;
      }
    }
  }
  
  // Estilos para modales
  .facilities-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
    
    .facility-item {
      display: flex;
      align-items: center;
      padding: 1rem;
      background: #F8F9FA;
      border-radius: 8px;
      transition: all 0.3s ease;
      
      &:hover {
        background: #E8F4F8;
        transform: translateX(5px);
      }
      
      .facility-icon {
        color: #0d2b45;
        margin-right: 0.75rem;
        font-size: 1.2rem;
      }
    }
  }
  
  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1rem;
  }

  .benefit-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e5eaee;
    transition: all 0.3s ease;

    &:hover {
      border-color: #9e9e9e;
    }

    &--animate {
      opacity: 0;
      animation: benefit-card-in 0.4s ease forwards;
    }

    .benefit-card__icon-wrap {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      border: 1px solid #e5eaee;
      border-radius: 10px;

      i {
        font-size: 1.25rem;
        color: #0d2b45;
      }
    }

    .benefit-card__content {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
    }

    .benefit-card__name {
      font-size: 1rem;
      font-weight: 700;
      color: #0d2b45;
      line-height: 1.3;
    }

    .benefit-card__qty {
      font-size: 0.9rem;
      color: #5a6d70;
      line-height: 1.35;
    }

    .benefit-card__tag {
      font-size: 0.75rem;
      color: #5a6d70;
      margin-top: 0.25rem;
    }
  }

  @keyframes benefit-card-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .savings-comparison-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #F3F6F9 0%, #E8F4F8 50%, #F3F6F9 100%);
    position: relative;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 30%;
      pointer-events: none;
      z-index: 0;
    }
    
    &::before {
      left: 0;
      background: radial-gradient(ellipse at left center, rgba(29, 90, 94, 0.4) 0%, rgba(29, 90, 94, 0) 70%);
    }
    
    &::after {
      right: 0;
      background: radial-gradient(ellipse at right center, rgba(34, 207, 224, 0.4) 0%, rgba(34, 207, 224, 0) 70%);
    }
    
    .container {
      position: relative;
      z-index: 1;
    }
    
    .section-header {
      margin-bottom: 3rem;
      margin-top: 3rem;
      padding-top: 2rem;
      
      .section-title {
        font-size: 3rem;
        font-weight: 700;
        color: $text-dark;
        margin-bottom: 1rem;
      }
      
      .section-subtitle {
        font-size: 1.5rem;
        color: $text-medium;
        font-weight: 400;
      }
    }
    
    .savings-table-wrapper {
      background: #ffffff;
      border-radius: 20px;
      padding: 3rem;
      box-shadow: 0 10px 40px rgba(13, 43, 69, 0.12), 0 5px 15px rgba(13, 43, 69, 0.08);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: 2px solid rgba(13, 43, 69, 0.1);
      height: 100%;
      display: flex;
      flex-direction: column;
      
      &:hover {
        box-shadow: 0 15px 50px rgba(13, 43, 69, 0.18), 0 8px 25px rgba(13, 43, 69, 0.12);
        transform: translateY(-5px);
      }
      
      .savings-table {
        margin: 0;
        width: 100%;
        
        thead {
          background: linear-gradient(135deg, rgba(13, 43, 69, 0.1) 0%, rgba(34, 207, 224, 0.05) 100%);
          
          th {
            border: none;
            padding: 1.8rem 1rem;
            font-weight: 700;
            color: $text-dark;
            font-size: 1.3rem;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-bottom: 3px solid rgba(13, 43, 69, 0.2);
          }
        }
        
        tbody {
          tr {
            border-bottom: 1px solid rgba(13, 43, 69, 0.1);
            transition: all 0.3s ease;
            
            &:hover {
              background: rgba(13, 43, 69, 0.03);
              transform: scale(1.01);
            }
            
            &:last-child {
              border-bottom: none;
            }
            
            td {
              padding: 1.8rem 1rem;
              border: none;
              vertical-align: middle;
              
              &.service-name {
                color: $text-dark;
                font-weight: 600;
                font-size: 1.3rem;
                line-height: 1.6;
              }
              
              &.without-plan {
                color: $text-medium;
                font-weight: 600;
                font-size: 1.35rem;
              }
              
              &.with-plan {
                color: $secondary-color;
                font-weight: 700;
                font-size: 1.4rem;
              }
            }
          }
        }
        
        tfoot {
          .total-row {
            background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
            color: white;
            transition: all 0.4s ease;
            border-top: 4px solid rgba(255, 255, 255, 0.2);
            
            &:hover {
              background: linear-gradient(135deg, #22cfe0 0%, #0d2b45 100%);
              transform: scale(1.01);
            }
            
            td {
              padding: 2.5rem 1rem;
              border: none;
              
              &.total-label {
                font-weight: 700;
                font-size: 1.6rem;
                text-transform: uppercase;
                letter-spacing: 1px;
                text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
              }
              
              &.total-without {
                span {
                  font-size: 2rem;
                  font-weight: 700;
                  opacity: 0.95;
                  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                }
              }
              
              &.total-with {
                strong {
                  font-size: 2.8rem;
                  font-weight: 700;
                  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
                  transition: transform 0.3s ease;
                  display: inline-block;
                  
                  &:hover {
                    transform: scale(1.15);
                  }
                }
              }
            }
          }
        }
      }
    }
    
    @media (max-width: 991px) {
      padding: 80px 0;
      
      .section-header {
        .section-title {
          font-size: 2.5rem;
        }
        
        .section-subtitle {
          font-size: 1.3rem;
        }
      }
      
      .savings-table-wrapper {
        padding: 2rem;
        
        .savings-table {
          thead th {
            padding: 1.2rem 0.8rem;
            font-size: 1rem;
          }
          
          tbody tr td {
            padding: 1rem 0.5rem;
            font-size: 0.9rem;
            
            &.service-name {
              font-size: 0.85rem;
            }
          }
          
          tfoot .total-row td {
            padding: 1.25rem 0.5rem;
            
            &.total-label {
              font-size: 0.95rem;
            }
            
            &.total-without .strikethrough {
              font-size: 1rem;
            }
            
            &.total-with strong {
              font-size: 1.5rem;
            }
          }
        }
      }
    }
    
    @media (max-width: 768px) {
      padding: 60px 0;
      
      .section-header {
        .section-title {
          font-size: 2rem;
        }
        
        .section-subtitle {
          font-size: 1.1rem;
        }
      }
      
      .savings-table-wrapper {
        padding: 1.5rem;
        
        .savings-table {
          thead th {
            padding: 1rem 0.5rem;
            font-size: 0.85rem;
          }
          
          tbody tr td {
            padding: 1rem 0.5rem;
            font-size: 0.9rem;
            
            &.service-name {
              font-size: 0.85rem;
            }
            
            &.without-plan {
              font-size: 0.95rem;
            }
            
            &.with-plan {
              font-size: 1rem;
            }
          }
          
          tfoot .total-row td {
            padding: 1.5rem 0.5rem;
            
            &.total-label {
              font-size: 1rem;
            }
            
            &.total-without span {
              font-size: 1.3rem;
            }
            
            &.total-with strong {
              font-size: 1.8rem;
            }
          }
        }
      }
    }
    
    .savings-summary {
      background: linear-gradient(to bottom right, #FFFFFF 0%, #FFFFFF 92%, #fafafa 95%, #f0f0f0 97%, #e0e0e0 98.5%, #c0c0c0 99.3%, #808080 99.7%, #1a1a1a 100%);
      border-radius: 20px;
      padding: 0;
      box-shadow: 0 10px 40px rgba(13, 43, 69, 0.12), 0 5px 15px rgba(13, 43, 69, 0.08);
      overflow: hidden;
      height: 100%;
      display: flex;
      flex-direction: column;
      border: 2px solid rgba(13, 43, 69, 0.15);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 50px rgba(13, 43, 69, 0.2), 0 8px 25px rgba(13, 43, 69, 0.12);
        border-color: #0d2b45;
      }
      
      .summary-content {
        padding: 2.5rem 2rem;
        color: $text-dark;
        flex: 1;
        display: flex;
        flex-direction: column;
        
        .savings-amount {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          line-height: 1;
          transition: all 0.3s ease;
          
          .savings-text {
            color: #000000;
            font-weight: 600;
            
            &.savings-title-small {
              font-size: 1.5rem;
            }
            
            &.savings-title-large {
              font-size: 3.75rem;
            }
          }
          
          .savings-number {
            color: $primary-color;
            font-size: 5.6rem;
            font-weight: 700;
            text-shadow: 0 2px 8px rgba(13, 43, 69, 0.15);
            
            &:hover {
              transform: scale(1.05);
              color: $primary-dark;
            }
          }
        }
        
        .summary-description {
          font-size: 1.3rem;
          line-height: 1.7;
          margin-bottom: 2rem;
          color: $text-medium;
        }
        
        .savings-percentage {
          margin-bottom: 2rem;
          padding: 0;
          background: transparent;
          border-radius: 15px;
          border: none;
          
          .percentage-content-wrapper {
            background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
            border-radius: 15px;
            padding: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1.5rem;
          }
          
          .percentage-content-left {
            flex: 1;
            width: 100%;
          }
          
          .percentage-label {
            font-size: 1.5rem;
            color: white;
            margin-bottom: 0.15rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .percentage-value-wrapper {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 0.5rem;
          }
          
          .percentage-value {
            font-size: 5.6rem;
            font-weight: 700;
            line-height: 1;
            color: white;
            transition: all 0.3s ease;
            
            &:hover {
              transform: scale(1.05);
            }
          }
          
          .percentage-icon {
            width: 120px;
            height: 120px;
            object-fit: contain;
            flex-shrink: 0;
          }
          
          .percentage-text {
            font-size: 1.2rem;
            color: white;
            line-height: 1.5;
            margin: 0;
          }
        }
        
        .btn-savings-cta {
          background: #FFFFFF;
          color: #0d2b45;
          border: none;
          border-radius: 12px;
          padding: 18px 35px;
          font-weight: 700;
          font-size: 1.4rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          margin-top: auto;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
          
          &:hover {
            transform: translateY(-3px) scale(1.02);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
            color: #0d2b45;
            text-decoration: none;
          }
          
          i {
            transition: transform 0.3s ease;
            font-size: 1.1rem;
          }
          
          &:hover i {
            transform: translateX(5px);
          }
        }
      }
    }
    
    @media (max-width: 991px) {
      .savings-summary {
        margin-top: 2rem;
        
        .summary-content {
          padding: 2rem 1.5rem;
          
          .savings-amount {
            .savings-text {
              &.savings-title-small {
                font-size: 1.25rem;
              }
              
              &.savings-title-large {
                font-size: 3.1rem;
              }
            }
            
            .savings-number {
              font-size: 4.4rem;
            }
          }
          
          .summary-description {
            font-size: 1.1rem;
          }
          
          .savings-percentage {
            .percentage-content-wrapper {
              flex-direction: column;
              padding: 1.5rem;
              gap: 1.5rem;
            }
            
            .percentage-label {
              font-size: 1.3rem;
            }
            
            .percentage-value-wrapper {
              gap: 0.75rem;
            }
            
            .percentage-value {
              font-size: 4.4rem;
            }
            
            .percentage-icon {
              width: 100px;
              height: 100px;
            }
            
            .percentage-text {
              font-size: 1rem;
            }
          }
          
          .btn-savings-cta {
            font-size: 1.2rem;
          }
        }
      }
    }
  }
  
  // What is Hispano Medical Plan Section
  .what-is-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #ffffff 0%, #F8F9FA 50%, #ffffff 100%);
    
    .section-description {
      font-size: 1.5rem;
    }
  }
  
  .cta-section {
    position: relative;
    min-height: 500px;
    padding: 100px 0;
    color: white;
    display: flex;
    align-items: center;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    overflow: hidden;
    
    .cta-overlay {
      display: none;
      
      @media (max-width: 768px) {
        width: 100%;
        background: linear-gradient(to right, 
          rgba(13, 43, 69, 0.85) 0%, 
          rgba(34, 207, 224, 0.7) 50%, 
          rgba(13, 43, 69, 0.6) 100%);
      }
    }
    
    .container {
      position: relative;
      z-index: 2;
    }
    
    .cta-content {
      max-width: 600px;
      margin: 0;
      padding-right: 30px;
      position: relative;
      z-index: 3;
    }
    
    .cta-title {
      font-size: 4rem;
      font-weight: 700;
      margin-bottom: 1rem;
      line-height: 1.2;
      color: white;
      text-shadow: 0 4px 15px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
      letter-spacing: -0.5px;
      white-space: nowrap;
      
      .cta-title-highlight {
        color: #22cfe0;
        text-shadow: 
          0.5px 0.5px 0px rgba(0, 0, 0, 0.3),
          -0.5px -0.5px 0px rgba(0, 0, 0, 0.3),
          0.5px -0.5px 0px rgba(0, 0, 0, 0.3),
          -0.5px 0.5px 0px rgba(0, 0, 0, 0.3),
          0 4px 15px rgba(34, 207, 224, 0.6), 
          0 2px 8px rgba(34, 207, 224, 0.4);
        -webkit-text-stroke: 0.3px rgba(0, 0, 0, 0.2);
        paint-order: stroke fill;
      }
    }
    
    .cta-subtitle-large {
      font-size: 4rem;
      font-weight: 700;
      margin-bottom: 2rem;
      line-height: 1.3;
      color: white;
      text-shadow: 0 4px 15px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
      letter-spacing: -0.3px;
    }
    
    .cta-subtitle {
      font-size: 2rem;
      margin-bottom: 3rem;
      color: white;
      line-height: 1.7;
      text-shadow: 0 3px 12px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
      font-weight: 500;
      letter-spacing: 0.1px;
    }
    
    .btn-cta-primary {
      background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
      color: white;
      border: none;
      border-radius: 50px;
      padding: 20px 50px;
      font-size: 1.4rem;
      font-weight: 700;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 10px 30px rgba(13, 43, 69, 0.45);
      letter-spacing: 0.5px;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      
      &:hover {
        background: linear-gradient(135deg, #123a5c 0%, #0d2b45 100%);
        transform: translateY(-4px) scale(1.02);
        box-shadow: 0 15px 40px rgba(13, 43, 69, 0.55);
        color: white;
        text-decoration: none;
      }
      
      i {
        transition: transform 0.3s ease;
        font-size: 1.4rem;
        margin-left: 10px;
      }
      
      &:hover i {
        transform: translateX(8px);
      }
    }
    
    @media (max-width: 991px) {
      min-height: 450px;
      padding: 80px 0;
      
      .cta-content {
        padding-right: 20px;
      }
      
      .cta-title {
        font-size: 3rem;
        white-space: normal;
      }
      
      .cta-subtitle-large {
        font-size: 3rem;
      }
      
      .cta-subtitle {
        font-size: 1.5rem;
      }
      
      .btn-cta-primary {
        font-size: 1.2rem;
        padding: 18px 45px;
      }
    }
    
    @media (max-width: 768px) {
      min-height: 400px;
      padding: 60px 0;
      
      .cta-content {
        padding-right: 0;
        max-width: 100%;
      }
      
      .cta-title {
        font-size: 2.5rem;
        margin-bottom: 1rem;
        white-space: normal;
      }
      
      .cta-subtitle-large {
        font-size: 2.5rem;
        margin-bottom: 1.5rem;
      }
      
      .cta-subtitle {
        font-size: 1.3rem;
        margin-bottom: 2.5rem;
      }
      
      .btn-cta-primary {
        font-size: 1.15rem;
        padding: 16px 40px;
        padding: 15px 35px;
        font-size: 1.1rem;
        width: 100%;
        justify-content: center;
      }
    }
  }
  
  // What is Hispano Medical Plan Section
  .what-is-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #ffffff 0%, #F8F9FA 50%, #ffffff 100%);
    
    .section-description {
      font-size: 1.5rem;
      color: #2D4A4D;
      max-width: 900px;
      margin: 1.5rem auto 3rem;
      line-height: 1.8;
      font-weight: 500;
      
      &.section-description-primary {
        color: #0d2b45;
      }
    }
    
    .feature-card {
      background: white;
      border-radius: 15px;
      padding: 2.5rem;
      text-align: center;
      box-shadow: 0 5px 20px rgba(13, 43, 69, 0.08);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      height: 100%;
      border: 2px solid #E5EAEE;
      
      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 15px 40px rgba(13, 43, 69, 0.25);
        border-color: #0d2b45;
      }
      
      .feature-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;
        transition: all 0.3s ease;
        padding: 10px;
        
        i {
          font-size: 2rem;
          color: white;
        }
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      
      &:hover .feature-icon {
        transform: scale(1.1) rotate(5deg);
      }
      
      .feature-title {
        font-size: 2rem;
        font-weight: 700;
        color: #2D4A4D;
        margin-bottom: 1.25rem;
        line-height: 1.3;
      }
      
      .feature-text {
        font-size: 1.25rem;
        color: #2D4A4D;
        line-height: 1.8;
        margin-bottom: 1.5rem;
        font-weight: 500;
      }
      
      .btn-feature-link {
        color: #0d2b45;
        font-weight: 600;
        font-size: 1.2rem;
        text-decoration: none;
        transition: all 0.3s ease;
        padding: 12px 30px;
        border-radius: 25px;
        background: white;
        border: 2px solid #0d2b45;
        display: inline-flex;
        align-items: center;
        box-shadow: 0 4px 15px rgba(13, 43, 69, 0.2), 0 2px 8px rgba(13, 43, 69, 0.1);
        
        &:hover {
          color: white;
          background: #0d2b45;
          border-color: #0d2b45;
          text-decoration: none;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(13, 43, 69, 0.3), 0 4px 12px rgba(13, 43, 69, 0.15);
          
          i {
            transform: translateX(5px);
          }
        }
        
        i {
          transition: transform 0.3s ease;
        }
      }
    }
  }
  
  // How It Works Section
  .how-it-works-section {
    padding: 120px 0;
    background: linear-gradient(180deg, #123a5c 0%, #22cfe0 100%);
    color: white;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>');
      opacity: 0.3;
    }
    
    .section-header {
      position: relative;
      z-index: 1;
    }
    
    .section-title {
      color: white;
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 4rem;
      letter-spacing: -0.5px;
      text-shadow: 0 4px 15px rgba(13, 43, 69, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
    }
    
    .row {
      display: flex;
      align-items: stretch;
    }
    
    .step-card {
      text-align: center;
      position: relative;
      padding: 3rem 2rem;
      z-index: 1;
      border-radius: 15px;
      transition: all 0.3s ease;
      border: 2px solid #0d2b45;
      box-shadow: 0 4px 12px rgba(34, 207, 224, 0.3);
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      
      &.step-card-dark {
        background: linear-gradient(180deg, #123a5c 0%, #22cfe0 100%);
        color: white;
      }
      
      &.step-card-light {
        background: linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 92%, #fafafa 95%, #f0f0f0 97%, #e0e0e0 98.5%, #c0c0c0 99.3%, #808080 99.7%, #2C2C2C 100%);
        color: #0d2b45;
      }
      
      .step-icon {
        width: 150px;
        height: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 2rem;
        transition: all 0.3s ease;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      
      .step-title {
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 1.5rem;
        line-height: 1.3;
      }
      
      .step-text {
        font-size: 1.5rem;
        line-height: 1.7;
        margin: 0;
      }
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 6px 18px rgba(34, 207, 224, 0.4);
        
        .step-icon {
          transform: scale(1.05);
        }
      }
      
      &.step-card-dark {
        .step-title,
        .step-text {
          color: white;
        }
      }
      
      &.step-card-light {
        .step-title {
          color: #0d2b45;
        }
        
        .step-text {
          color: #2D4A4D;
        }
      }
    }
  }
  
  // Service Areas Section
  .service-areas-section {
    padding: 120px 0;
    background: white;
    
    .section-header {
      margin-bottom: 4rem;
      margin-top: 3rem;
      padding-top: 2rem;
      
      .section-title {
        font-size: 3rem;
        font-weight: 700;
        color: #0d2b45;
        margin-bottom: 1.5rem;
        letter-spacing: -0.5px;
        line-height: 1.2;
      }
      
      .section-subtitle {
        font-size: 1.3rem;
        color: #5A6D70;
        max-width: 700px;
        margin: 0 auto;
        line-height: 1.7;
      }
    }
    
    .location-card {
      background: white;
      border-radius: 20px;
      padding: 2.5rem;
      box-shadow: 0 8px 30px rgba(13, 43, 69, 0.1);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: 3px solid #E5EAEE;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      word-wrap: break-word;
      
      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 15px 45px rgba(13, 43, 69, 0.2);
        border-color: #0d2b45;
      }
      
      .location-header {
        margin-bottom: 1.5rem;
        padding-bottom: 1.5rem;
        border-bottom: 3px solid #F8F9FA;
        
        .location-name {
          font-size: 1.8rem;
          font-weight: 700;
          color: #0d2b45;
          margin: 0;
          letter-spacing: -0.3px;
        }
      }
      
      .location-details {
        flex: 1;
        margin-bottom: 1.5rem;
        min-width: 0;
        overflow: hidden;
        
        .location-detail-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.25rem;
          min-width: 0;
          width: 100%;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .location-icon {
            color: #0d2b45;
            font-size: 1.1rem;
            margin-top: 0.2rem;
            flex-shrink: 0;
            width: 20px;
            text-align: center;
          }
          
          .location-text {
            font-size: 1rem;
            color: #2D4A4D;
            line-height: 1.6;
            flex: 1;
            word-break: break-word;
            overflow-wrap: break-word;
            min-width: 0;
            max-width: 100%;
          }
          
          .location-link {
            font-size: 1rem;
            color: #0d2b45;
            text-decoration: none;
            transition: all 0.3s ease;
            line-height: 1.6;
            word-break: break-word;
            overflow-wrap: break-word;
            min-width: 0;
            flex: 1;
            max-width: 100%;
            
            &:hover {
              color: #123a5c;
              text-decoration: underline;
            }
          }
        }
      }
      
      .location-actions {
        padding-top: 1.5rem;
        border-top: 3px solid #F8F9FA;
        
        .btn-location-directions {
          width: 100%;
          background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 14px 24px;
          font-size: 1.1rem;
          font-weight: 600;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 25px rgba(13, 43, 69, 0.3);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          letter-spacing: 0.3px;
          
          &:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 35px rgba(13, 43, 69, 0.4);
            background: linear-gradient(135deg, #123a5c 0%, #0d2b45 100%);
            color: white;
            text-decoration: none;
          }
          
          &:active {
            transform: translateY(-1px);
          }
          
          i {
            font-size: 1rem;
          }
        }
      }
    }
    
    .service-areas-content-wrapper {
      position: relative;
      min-height: 600px;
      padding: 2rem 0;
    }
    
    .texas-map-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 0;
      pointer-events: none;
      overflow: hidden;
      
      .texas-map-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center right;
        opacity: 0.3;
        transform: scale(1.2);
      }
    }
    
    .locations-row {
      position: relative;
      z-index: 1;
      display: flex;
      flex-wrap: wrap;
      margin-left: -15px;
      margin-right: -15px;
      
      .location-col {
        flex: 0 0 20%;
        max-width: 20%;
        padding-left: 15px;
        padding-right: 15px;
        
        @media (max-width: 991px) {
          flex: 0 0 50%;
          max-width: 50%;
        }
        
        @media (max-width: 768px) {
          flex: 0 0 100%;
          max-width: 100%;
        }
      }
      
      .location-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(5px);
      }
    }
  }
  
  
  // Contact Info Section
  .contact-info-section {
    padding: 100px 0;
    background: linear-gradient(135deg, #0d2b45 0%, #0d2b45 75%, #22cfe0 100%);
    color: white;
    
    .section-header {
      margin-bottom: 4rem;
      
      .section-title {
        color: white;
        font-size: 3.5rem;
        font-weight: 700;
        margin-bottom: 1.5rem;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }
      
      .section-subtitle {
        color: white;
        font-size: 1.8rem;
        font-weight: 500;
        line-height: 1.6;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }
    
    .contact-card {
      background: white;
      border-radius: 15px;
      padding: 2rem;
      text-align: center;
      transition: all 0.3s ease;
      border: 2px solid #E5EAEE;
      height: 100%;
      box-shadow: 0 5px 20px rgba(13, 43, 69, 0.08);
      
      &:hover {
        transform: translateY(-5px);
        background: white;
        border-color: #0d2b45;
        box-shadow: 0 10px 30px rgba(40, 167, 69, 0.2);
      }
      
      .contact-icon {
        width: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;
        transition: all 0.3s ease;
        
        .contact-icon-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        i {
          font-size: 2rem;
          color: white;
        }
      }
      
      &:hover .contact-icon {
        transform: scale(1.1);
      }
      
      .contact-title {
        font-size: 1.8rem;
        font-weight: 700;
        color: #0d2b45;
        margin-bottom: 1rem;
      }
      
      .contact-info {
        font-size: 1.5rem;
        color: #2D4A4D;
        margin-bottom: 0.5rem;
        font-weight: 600;
      }
      
      .contact-hours {
        font-size: 1.2rem;
        color: #2D4A4D;
        margin: 0;
        font-weight: 500;
      }
      
      .contact-link {
        margin-top: 0.75rem;
        
        a {
          color: #0d2b45;
          text-decoration: none;
          font-weight: 600;
          font-size: 1.05rem;
          transition: color 0.3s ease;
          
          &:hover {
            color: #123a5c;
            text-decoration: underline;
          }
          
          i {
            margin-right: 0.5rem;
          }
        }
      }
    }
  }
  
  .faq-section {
    padding: 100px 0;
    background: linear-gradient(180deg, #ffffff 0%, #F3F6F9 50%, #ffffff 100%);
    
    .section-header {
      margin-bottom: 4rem;
      margin-top: 3rem;
      padding-top: 2rem;
    }
    
    .faq-title {
      font-size: 4rem;
      font-weight: 700;
      color: #0d2b45;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }
    
    .faq-subtitle {
      font-size: 1.5rem;
      color: #2D4A4D;
      max-width: 800px;
      margin: 0 auto;
      font-weight: 500;
      line-height: 1.7;
    }
    
    .faq-accordion {
        .faq-item {
          margin-bottom: 1.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #F8F9FA 100%);
          border: 2px solid #E5EAEE;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          
          &:hover {
            border-color: #0d2b45;
            box-shadow: 0 5px 15px rgba(13, 43, 69, 0.2);
            background: linear-gradient(135deg, #ffffff 0%, #E8F4F8 100%);
          }
        
        .faq-question {
          padding: 1.5rem 2rem;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;
          background: white;
          
          &:hover {
            background: #F8F9FA;
          }
          
          &.active {
            background: linear-gradient(135deg, #F8F9FA 0%, #E8F4F8 100%);
            border-bottom: 2px solid #E5EAEE;
          }
          
          .faq-question-content {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
            
            .faq-number {
              font-size: 1.3rem;
              font-weight: 700;
              color: #0d2b45;
              min-width: 30px;
            }
            
            .faq-question-text {
              font-size: 1.5rem;
              font-weight: 600;
              color: #2D4A4D;
              line-height: 1.5;
            }
          }
          
          .faq-arrow {
            font-size: 1.2rem;
            color: #0d2b45;
            transition: transform 0.3s ease;
            flex-shrink: 0;
            margin-left: 15px;
            
            &.rotated {
              transform: rotate(180deg);
            }
          }
        }
        
        .faq-answer {
          padding: 0 2rem 1.5rem 2rem;
          background: white;
          
          p {
            font-size: 1.3rem;
            color: #5A6D70;
            line-height: 1.8;
            margin: 0;
            padding-left: 45px;
          }
        }
      }
    }
    
    // Animación de transición para el contenido
    .slide-fade-enter-active {
      transition: all 0.3s ease-out;
    }
    
    .slide-fade-leave-active {
      transition: all 0.3s ease-in;
    }
    
    .slide-fade-enter {
      transform: translateY(-10px);
      opacity: 0;
    }
    
    .slide-fade-leave-to {
      transform: translateY(-10px);
      opacity: 0;
    }
    
    @media (max-width: 991px) {
      padding: 80px 0;
      
      .faq-title {
        font-size: 2.5rem;
      }
    }
    
    @media (max-width: 991px) {
      padding: 80px 0;
      
      .what-is-section,
      .how-it-works-section,
      .service-areas-section,
      .contact-info-section,
      .detailed-plans-section {
        padding: 80px 0;
        
        .section-header {
          .section-title {
            font-size: 3rem;
          }
          
          .section-subtitle {
            font-size: 1.5rem;
          }
        }
      }
      
      .detailed-plans-section {
        .section-header {
          .section-title {
            color: #0d2b45;
          }
          
          .section-subtitle {
            color: #0d2b45;
          }
        }
      }
      
      .how-it-works-section {
        .section-title {
          font-size: 2.5rem;
        }
        
        .step-card {
          padding: 2.5rem 1.5rem;
          
          .step-number {
            width: 55px;
            height: 55px;
            font-size: 1.5rem;
            top: -22px;
          }
          
          .step-icon {
            width: 110px;
            height: 110px;
            margin: 2rem auto 1.5rem;
            
            i {
              font-size: 2.5rem;
            }
          }
          
          .step-title {
            font-size: 1.75rem;
          }
          
          .step-text {
            font-size: 1.2rem;
          }
        }
      }
      
      .contact-info-section {
        .section-header {
          .section-title {
            font-size: 3rem;
            color: white;
          }
          
          .section-subtitle {
            font-size: 1.5rem;
            color: white;
          }
        }
      }
      
    }
    
    @media (max-width: 768px) {
      padding: 60px 0;
      
      .what-is-section,
      .how-it-works-section,
      .service-areas-section,
      .contact-info-section,
      .detailed-plans-section {
        padding: 60px 0;
      }
      
      .detailed-plans-section {
        .section-header {
          .section-title {
            font-size: 2.5rem;
            color: #0d2b45;
          }
          
          .section-subtitle {
            font-size: 1.3rem;
            color: #0d2b45;
          }
        }
      }
      
      .how-it-works-section {
        .section-title {
          font-size: 2rem;
        }
        
        .step-card {
          padding: 2.5rem 1.5rem;
          margin-bottom: 3rem;
          
          .step-number {
            width: 50px;
            height: 50px;
            font-size: 1.4rem;
            top: -20px;
          }
          
          .step-icon {
            width: 100px;
            height: 100px;
            margin: 2rem auto 1.5rem;
            
            i {
              font-size: 2.2rem;
            }
          }
          
          .step-title {
            font-size: 1.5rem;
          }
          
          .step-text {
            font-size: 1.1rem;
          }
        }
      }
      
      .what-is-section {
        .section-description {
          font-size: 1rem;
          margin-bottom: 2rem;
        }
        
        .feature-card {
          padding: 2rem 1.5rem;
          
          .feature-icon {
            width: 70px;
            height: 70px;
            padding: 8px;
            
            i {
              font-size: 1.8rem;
            }
            
            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
          }
          
          .feature-title {
            font-size: 1.3rem;
          }
          
          .btn-feature-link {
            font-size: 1.1rem;
            padding: 10px 25px;
          }
        }
      }
      
      .how-it-works-section {
        .section-title {
          font-size: 2rem;
          margin-bottom: 2rem;
        }
        
        .step-card {
          padding: 1.5rem;
          
          .step-icon {
            width: 80px;
            height: 80px;
            margin: 1.5rem auto 1rem;
            
            i {
              font-size: 2rem;
            }
          }
          
          .step-title {
            font-size: 1.3rem;
          }
        }
      }
      
      .service-areas-section {
        padding: 100px 0;
        
        .section-header {
          margin-bottom: 3rem;
          
          .section-title {
            font-size: 2.5rem;
          }
          
          .section-subtitle {
            font-size: 1.2rem;
          }
        }
        
        .texas-map-wrapper {
          position: relative;
          top: auto;
          margin-top: 3rem;
          
          .texas-map-image {
            width: 100%;
            transform: translateX(0);
          }
        }
        
        .location-card {
          padding: 2rem 1.5rem;
          
          .location-header {
            margin-bottom: 1.25rem;
            padding-bottom: 1.25rem;
            
            .location-name {
              font-size: 1.5rem;
            }
          }
          
          .location-details {
            margin-bottom: 1.25rem;
            
            .location-detail-item {
              margin-bottom: 1rem;
              
              .location-icon {
                font-size: 1rem;
              }
              
              .location-text,
              .location-link {
                font-size: 0.95rem;
              }
            }
          }
          
          .location-actions {
            padding-top: 1.25rem;
            
            .btn-location-directions {
              padding: 12px 20px;
              font-size: 1rem;
            }
          }
        }
      }
      
      .detailed-plans-section {
        .section-header {
          .section-title {
            font-size: 2.5rem;
            color: #0d2b45;
          }
          
          .section-subtitle {
            font-size: 1.1rem;
            color: #0d2b45;
          }
        }
      }
      
      .contact-info-section {
        .section-header {
          .section-title {
            font-size: 2.5rem;
            color: white;
          }
          
          .section-subtitle {
            font-size: 1.1rem;
            color: white;
          }
        }
        
        .contact-card {
          padding: 1.5rem;
          
          .contact-icon {
            width: 80px;
            height: 80px;
            
            .contact-icon-image {
              width: 100%;
              height: 100%;
            }
            
            i {
              font-size: 1.8rem;
            }
          }
          
          .contact-title {
            font-size: 1.5rem;
          }
          
          .contact-info {
            font-size: 1.3rem;
          }
          
          .contact-hours {
            font-size: 1.1rem;
          }
          
          .contact-title {
            font-size: 1.2rem;
          }
        }
      }
      
      .faq-title {
        font-size: 2rem;
      }
      
      .faq-subtitle {
        font-size: 1rem;
      }
      
      .faq-accordion .faq-item {
        .faq-question {
          padding: 1.25rem 1.5rem;
          
          .faq-question-content {
            gap: 10px;
            
            .faq-number {
              font-size: 1.1rem;
              min-width: 25px;
            }
            
            .faq-question-text {
              font-size: 1.2rem;
            }
          }
          
          .faq-arrow {
            font-size: 1rem;
            margin-left: 10px;
          }
        }
        
        .faq-answer {
          padding: 0 1.5rem 1.25rem 1.5rem;
          
          p {
            font-size: 1.1rem;
            padding-left: 35px;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .landing-home {
    .hero-section {
      padding: 60px 0;
      
      .hero-title {
        font-size: 2.5rem;
        
        .title-highlight {
          font-size: 2.8rem;
        }
      }
      
      .hero-subtitle {
        font-size: 1.6rem;
      }
      
      .hero-description {
        font-size: 1.1rem;
      }
      
      .hero-buttons {
        .btn {
          display: block;
          width: 100%;
          margin-bottom: 1rem;
          
          &.ml-3 {
            margin-left: 0 !important;
          }
        }
      }
    }
    
    .service-areas-section {
      padding: 80px 0;
      
      .section-header {
        margin-bottom: 3rem;
        
        .section-title {
          font-size: 2rem;
        }
        
        .section-subtitle {
          font-size: 1.1rem;
        }
      }
      
      .texas-map-wrapper {
        position: relative;
        top: auto;
        margin-top: 2rem;
        
        .texas-map-image {
          width: 100%;
          transform: translateX(0);
        }
      }
      
      .location-card {
        padding: 2rem 1.5rem;
        
        .location-header {
          margin-bottom: 1.25rem;
          padding-bottom: 1.25rem;
          
          .location-name {
            font-size: 1.4rem;
          }
        }
        
        .location-details {
          margin-bottom: 1.25rem;
          
          .location-detail-item {
            margin-bottom: 1rem;
            
            .location-icon {
              font-size: 1rem;
            }
            
            .location-text,
            .location-link {
              font-size: 0.9rem;
            }
          }
        }
        
        .location-actions {
          padding-top: 1.25rem;
          
          .btn-location-directions {
            padding: 12px 20px;
            font-size: 1rem;
          }
        }
      }
    }
  }
}
</style>

<!-- Estilos para modal de beneficios (sin scoped: el modal se renderiza fuera del componente) -->
<style lang="scss">
.benefits-modal-title {
  color: #0d2b45 !important;
  font-weight: 700;
}

.marketing-benefits-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.85rem 1rem;
    margin-bottom: 0.65rem;
    background: #f8fafc;
    border-radius: 10px;
    border-left: 3px solid #22cfe0;

    b {
      color: #0d2b45;
      font-size: 0.98rem;
    }

    span {
      color: #5a6d70;
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
}

.modal-body .benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.modal-body .benefit-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5eaee;
  transition: all 0.3s ease;

  &:hover {
    border-color: #9e9e9e;
  }

  &.benefit-card--animate {
    opacity: 0;
    animation: benefit-card-modal-in 0.4s ease forwards;
  }

  .benefit-card__icon-wrap {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e5eaee;
    border-radius: 10px;

    i {
      font-size: 1.25rem;
      color: #0d2b45;
    }
  }

  .benefit-card__content {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  .benefit-card__name {
    font-size: 1rem;
    font-weight: 700;
    color: #0d2b45;
    line-height: 1.3;
  }

  .benefit-card__qty {
    font-size: 0.9rem;
    color: #5a6d70;
    line-height: 1.35;
  }

  .benefit-card__tag {
    font-size: 0.75rem;
    color: #5a6d70;
    margin-top: 0.25rem;
  }
}

@keyframes benefit-card-modal-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

